import { Typography, Stack } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Alert, C1AlertTitle } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CircleAlert } from 'lucide-react'
import { Any } from 'pbts/google/protobuf/any'
import { Status as Sts } from 'pbts/google/rpc/status'
import { FC, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'

type possibleErrorTypes = RpcError | null | undefined | unknown | SerializedErrorType

export interface APIErrorProps extends Omit<React.ComponentProps<typeof C1Alert>, 'title'> {
    err?: possibleErrorTypes
    title?: ReactNode
    message?: string
    code?: string
}

export const APIMetaDataArray = (err: possibleErrorTypes): Any[] => {
    if (!err) {
        return []
    }

    if (typeof err === 'object') {
        const meta = 'meta' in err ? err.meta : ''
        if (meta) {
            const bin = meta['grpc-status-details-bin']
            if (typeof bin === 'string') {
                const buf = new Uint8Array(Buffer.from(bin, 'base64'))
                return Sts.fromBinary(buf).details
            }
        }
    }
    return []
}

export const APIErrorText = (err: possibleErrorTypes): string => {
    if (!err) {
        return ''
    }

    if (typeof err === 'object') {
        const message = 'message' in err && err.message
        const code = 'code' in err ? err.code : ''
        if (message && code) {
            return `${message} (${code})`
        }
        if (message) {
            return message.toString()
        }
    }

    return JSON.stringify(err)
}

export const MinimalAPIError: FC<{ err: ReactNode }> = ({ err }) => {
    return <Stack direction="row" spacing={1} alignItems="center">
        <C1Icon icon={CircleAlert} sx={{ color: 'error.main' }} />
        <Typography color="error.dark">{err}</Typography>
    </Stack>
}

export const APIError = ({ err, title, message, code, ...props }: APIErrorProps): React.ReactElement | null => {
    if ((err === null || err === undefined) && !message) {
        return null
    }

    if (!title) {
        title = <FormattedMessage id="component_api_error" defaultMessage="Internal Error" />
    }

    if (message) {
        return (
            <C1Alert severity="error" {...props}>
                <C1AlertTitle>{title}</C1AlertTitle>
                {code ? `${code}: ${message}` : message}
            </C1Alert>
        )
    }

    return (
        <C1Alert severity="error" {...props}>
            <C1AlertTitle>{title}</C1AlertTitle>
            {APIErrorText(err)}
        </C1Alert>
    )
}

export const MultiAPIError = ({ errs, ...props }: { errs: possibleErrorTypes[] } & Omit<React.ComponentProps<typeof C1Alert>, 'title'>): React.ReactNode => {
    if (!errs || errs.length === 0) {
        return null
    }

    return <Stack gap={2}>
        {errs.map((err, index) => <APIError key={index} err={err} {...props} />)}
    </Stack>
}
